import React, { useState } from "react";
import { motion } from "framer-motion";

const PRODUCTS = [
  {
    name: "RecurPlus",
    tagline: "Know exactly what you're paying for — and why.",
    description:
      "A subscription management platform built for businesses that want full visibility into their recurring costs, contracts, and renewals.",
    category: "SUBSCRIPTION MANAGEMENT",
    domain: "recurplus.app",
    index: "01",
    testId: "product-card-recurplus",
  },
  {
    name: "GRC Verify",
    tagline: "Policy meets reality — automatically.",
    description:
      "An intelligent compliance engine that checks documentation against industry regulations and frameworks, so GRC teams spend less time auditing and more time acting.",
    category: "GOVERNANCE · RISK · COMPLIANCE",
    domain: null,
    index: "02",
    testId: "product-card-grcverify",
  },
  {
    name: "ConnectGRC",
    tagline: "The professional network the GRC world was waiting for.",
    description:
      "A community platform and talent marketplace for GRC professionals — pre-vetted credentials, trusted profiles, and a hiring experience that actually reflects expertise.",
    category: "COMMUNITY · TALENT MARKETPLACE",
    domain: "connectgrc.com",
    index: "03",
    testId: "product-card-connectgrc",
  },
];

// Pulsing status dot
const PulseDot = () => (
  <motion.span
    animate={{ opacity: [1, 0.3, 1] }}
    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
    style={{
      display: "inline-block",
      width: "6px",
      height: "6px",
      borderRadius: "50%",
      backgroundColor: "#FF3B00",
      flexShrink: 0,
    }}
  />
);

const ProductCard = ({ product, cardIndex }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      data-testid={product.testId}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{
        delay: cardIndex * 0.14,
        duration: 0.9,
        ease: [0.22, 1, 0.36, 1],
      }}
      viewport={{ once: true, margin: "-60px" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "#0D0D0D" : "#0A0A0A",
        border: `1px solid ${hovered ? "#FF3B00" : "#1A1A1A"}`,
        padding: "clamp(28px, 4vw, 48px)",
        display: "flex",
        flexDirection: "column",
        gap: "0",
        transition: "background 0.35s ease, border-color 0.35s ease",
        cursor: "default",
      }}
    >
      {/* Index + Status row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "32px",
        }}
      >
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "11px",
            color: "#FF3B00",
            letterSpacing: "0.2em",
          }}
        >
          [{product.index}]
        </span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <PulseDot />
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "9px",
              color: "#444444",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
            }}
          >
            IN DEVELOPMENT
          </span>
        </div>
      </div>

      {/* Product name */}
      <h3
        style={{
          fontFamily: "'Cabinet Grotesk', sans-serif",
          fontWeight: 800,
          fontSize: "clamp(1.8rem, 2.5vw, 2.4rem)",
          color: "#F4F4F0",
          margin: "0 0 8px 0",
          letterSpacing: "-0.03em",
          lineHeight: 1,
        }}
      >
        {product.name}
      </h3>

      {/* Tagline */}
      <p
        style={{
          fontFamily: "'Cabinet Grotesk', sans-serif",
          fontWeight: 500,
          fontSize: "clamp(0.95rem, 1.2vw, 1.1rem)",
          color: hovered ? "#CCCCCC" : "#999999",
          margin: "0 0 20px 0",
          letterSpacing: "-0.01em",
          lineHeight: 1.3,
          transition: "color 0.35s ease",
          fontStyle: "italic",
        }}
      >
        {product.tagline}
      </p>

      {/* Description */}
      <p
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: "14px",
          color: "#666666",
          lineHeight: 1.75,
          margin: "0 0 auto 0",
          flexGrow: 1,
          paddingBottom: "28px",
        }}
      >
        {product.description}
      </p>

      {/* Footer */}
      <div
        style={{
          borderTop: "1px solid #1A1A1A",
          paddingTop: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "9px",
            color: "#333333",
            letterSpacing: "0.15em",
          }}
        >
          {product.category}
        </span>
        {product.domain && (
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "9px",
              color: hovered ? "#555555" : "#2A2A2A",
              letterSpacing: "0.12em",
              transition: "color 0.35s ease",
            }}
          >
            {product.domain}
          </span>
        )}
      </div>
    </motion.div>
  );
};

const OurProducts = () => (
  <section
    data-testid="what-we-do-section"
    id="what-we-do"
    style={{
      padding: "clamp(80px, 12vw, 160px) clamp(24px, 6vw, 96px)",
      borderTop: "1px solid #1A1A1A",
    }}
  >
    {/* Section header */}
    <div
      className="flex flex-col md:flex-row md:items-end"
      style={{ gap: "clamp(20px, 5vw, 64px)", marginBottom: "clamp(48px, 7vw, 90px)" }}
    >
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        viewport={{ once: true }}
        style={{ flexShrink: 0 }}
      >
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "11px",
            color: "#FF3B00",
            letterSpacing: "0.22em",
            display: "block",
            marginBottom: "14px",
          }}
        >
          [01]
        </span>
        <h2
          style={{
            fontFamily: "'Cabinet Grotesk', sans-serif",
            fontWeight: 800,
            fontSize: "clamp(2.8rem, 5vw, 5rem)",
            lineHeight: 0.95,
            letterSpacing: "-0.03em",
            color: "#F4F4F0",
            textTransform: "uppercase",
            margin: 0,
          }}
        >
          Our<br />Products
        </h2>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.8 }}
        viewport={{ once: true }}
        style={{ maxWidth: "520px" }}
      >
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "16px",
            color: "#666666",
            lineHeight: 1.7,
            margin: "0 0 16px 0",
          }}
        >
          Three focused products, each built to solve a distinct problem with
          precision. No compromise, no shortcuts.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <PulseDot />
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "10px",
              color: "#444444",
              letterSpacing: "0.18em",
            }}
          >
            ALL THREE PRODUCTS CURRENTLY IN ACTIVE DEVELOPMENT
          </span>
        </div>
      </motion.div>
    </div>

    {/* Product cards */}
    <div
      style={{ display: "grid", gap: "1px", background: "#1A1A1A" }}
      className="grid-cols-1 md:grid-cols-3"
    >
      {PRODUCTS.map((product, i) => (
        <ProductCard key={product.name} product={product} cardIndex={i} />
      ))}
    </div>
  </section>
);

export default OurProducts;
